import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner, Badge, Form, Modal } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import CreateTagDefinitionModal from "../components/CreateTagDefinitionModal";
import EditTagDefinitionModal from "../components/EditTagDefinitionModal";

import "../components/SchoolAdmin.css";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";

import {
  whoami,
  getTagDefinitions,
  getResolvedTags,
  updateEntityTag,
  createTagDefinition,
  updateTagDefinition,
  deleteTagDefinition
} from "../services/APIClient";
import {
  TCSD_WORKFLOW_ELEMENT_TYPE,
  TCSD_WORKFLOW_TAG_NAME,
  TCSD_WORKFLOWS
} from "../data/tcsdWorkflows";
import {
  TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
  TEACHER_DASHBOARD_NAVIGATION_ITEMS,
  TEACHER_DASHBOARD_NAVIGATION_TAG_NAME,
  TEACHER_DASHBOARD_REPORTING_ITEMS,
  TEACHER_DASHBOARD_REPORTING_SUBHEADING_TAG_NAME,
  TEACHER_DASHBOARD_REPORTING_TITLE_TAG_NAME
} from "../data/teacherDashboardNavigation";

function TagManagement() {
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("John Doe");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [tagDefinitions, setTagDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tcsdTagDefinition, setTcsdTagDefinition] = useState(null);
  const [tcsdWorkflowValues, setTcsdWorkflowValues] = useState({});
  const [tcsdLoading, setTcsdLoading] = useState(true);
  const [tcsdError, setTcsdError] = useState(null);
  const [savingWorkflowIds, setSavingWorkflowIds] = useState(new Set());
  const [teacherNavigationTagDefinition, setTeacherNavigationTagDefinition] = useState(null);
  const [teacherNavigationValues, setTeacherNavigationValues] = useState({});
  const [teacherNavigationLoading, setTeacherNavigationLoading] = useState(true);
  const [teacherNavigationError, setTeacherNavigationError] = useState(null);
  const [savingTeacherNavigationIds, setSavingTeacherNavigationIds] = useState(new Set());
  const [reportingTitleTagDefinition, setReportingTitleTagDefinition] = useState(null);
  const [reportingSubheadingTagDefinition, setReportingSubheadingTagDefinition] = useState(null);
  const [reportingCopyDrafts, setReportingCopyDrafts] = useState({});
  const [savingReportingCopyIds, setSavingReportingCopyIds] = useState(new Set());
  const [reportingExpanded, setReportingExpanded] = useState(true);
  
  const [showModal, setShowModal] = useState({
    createTag: false,
    editTag: false,
    tcsdWorkflows: false,
    teacherDashboardNavigation: false,
  });
  
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });
  }, []);

  useEffect(() => {
    fetchTagDefinitions();
  }, []);

  const fetchTagDefinitions = async () => {
    setLoading(true);
    setTcsdLoading(true);
    setTeacherNavigationLoading(true);
    setError(null);
    setTcsdError(null);
    setTeacherNavigationError(null);
    let definitions;

    try {
      const data = await getTagDefinitions();
      definitions = Array.isArray(data) ? data : [];
      setTagDefinitions(definitions);
    } catch (err) {
      console.error("Error fetching tag definitions:", err);
      setError(`Failed to load tag definitions: ${err.message}`);
      setTcsdError(`Failed to load TCSD workflow tags: ${err.message}`);
      setTeacherNavigationError(`Failed to load Teacher Dashboard navigation tags: ${err.message}`);
      setLoading(false);
      setTcsdLoading(false);
      setTeacherNavigationLoading(false);
      return;
    }

    setLoading(false);
    const workflowDefinition = definitions.find(
      (definition) => definition.tag_name === TCSD_WORKFLOW_TAG_NAME
    );
    const navigationDefinition = definitions.find(
      (definition) => definition.tag_name === TEACHER_DASHBOARD_NAVIGATION_TAG_NAME
    );
    const reportingTitleDefinition = definitions.find(
      (definition) => definition.tag_name === TEACHER_DASHBOARD_REPORTING_TITLE_TAG_NAME
    );
    const reportingSubheadingDefinition = definitions.find(
      (definition) => definition.tag_name === TEACHER_DASHBOARD_REPORTING_SUBHEADING_TAG_NAME
    );

    if (!workflowDefinition) {
      setTcsdError(`Tag definition '${TCSD_WORKFLOW_TAG_NAME}' was not found`);
      setTcsdLoading(false);
    } else {
      setTcsdTagDefinition(workflowDefinition);
      try {
        const values = await getResolvedTags(
          TCSD_WORKFLOW_ELEMENT_TYPE,
          TCSD_WORKFLOW_TAG_NAME,
          TCSD_WORKFLOWS.map((workflow) => workflow.id)
        );
        setTcsdWorkflowValues(values);
      } catch (err) {
        console.error("Error fetching TCSD workflow tags:", err);
        setTcsdError(`Failed to load TCSD workflow tags: ${err.message}`);
      } finally {
        setTcsdLoading(false);
      }
    }

    if (!navigationDefinition || !reportingTitleDefinition || !reportingSubheadingDefinition) {
      const missingNames = [
        !navigationDefinition && TEACHER_DASHBOARD_NAVIGATION_TAG_NAME,
        !reportingTitleDefinition && TEACHER_DASHBOARD_REPORTING_TITLE_TAG_NAME,
        !reportingSubheadingDefinition && TEACHER_DASHBOARD_REPORTING_SUBHEADING_TAG_NAME,
      ].filter(Boolean);
      setTeacherNavigationError(
        `Tag definition${missingNames.length === 1 ? "" : "s"} '${missingNames.join("', '")}' ${missingNames.length === 1 ? "was" : "were"} not found`
      );
      setTeacherNavigationLoading(false);
    } else {
      setTeacherNavigationTagDefinition(navigationDefinition);
      setReportingTitleTagDefinition(reportingTitleDefinition);
      setReportingSubheadingTagDefinition(reportingSubheadingDefinition);
      try {
        const reportingItemIds = TEACHER_DASHBOARD_REPORTING_ITEMS.map((item) => item.id);
        const [values, titles, subheadings] = await Promise.all([
          getResolvedTags(
            TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
            TEACHER_DASHBOARD_NAVIGATION_TAG_NAME,
            TEACHER_DASHBOARD_NAVIGATION_ITEMS.map((item) => item.id)
          ),
          getResolvedTags(
            TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
            TEACHER_DASHBOARD_REPORTING_TITLE_TAG_NAME,
            reportingItemIds
          ),
          getResolvedTags(
            TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
            TEACHER_DASHBOARD_REPORTING_SUBHEADING_TAG_NAME,
            reportingItemIds
          ),
        ]);
        setTeacherNavigationValues({ ...values, home: "ON" });
        setReportingCopyDrafts(Object.fromEntries(
          TEACHER_DASHBOARD_REPORTING_ITEMS.map((item) => [
            item.id,
            {
              title: titles[item.id]?.trim() || item.defaultTitle,
              subheading: subheadings[item.id]?.trim() || item.defaultSubheading,
            },
          ])
        ));
      } catch (err) {
        console.error("Error fetching Teacher Dashboard navigation tags:", err);
        setTeacherNavigationError(
          `Failed to load Teacher Dashboard navigation tags: ${err.message}`
        );
      } finally {
        setTeacherNavigationLoading(false);
      }
    }
  };

  const handleTeacherNavigationToggle = async (navigationItem) => {
    if (
      navigationItem.locked ||
      !teacherNavigationTagDefinition ||
      savingTeacherNavigationIds.has(navigationItem.id)
    ) return;

    const previousValue = teacherNavigationValues[navigationItem.id] || "ON";
    const nextValue = previousValue === "ON" ? "OFF" : "ON";
    setTeacherNavigationValues((values) => ({
      ...values,
      [navigationItem.id]: nextValue,
      home: "ON"
    }));
    setSavingTeacherNavigationIds((ids) => new Set(ids).add(navigationItem.id));
    setTeacherNavigationError(null);

    try {
      await updateEntityTag(
        TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
        navigationItem.id,
        teacherNavigationTagDefinition.id,
        nextValue,
        name
      );
      setSuccessMessage(`${navigationItem.label} switched ${nextValue}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setTeacherNavigationValues((values) => ({
        ...values,
        [navigationItem.id]: previousValue,
        home: "ON"
      }));
      setTeacherNavigationError(
        `Failed to update ${navigationItem.label}: ${err.message}`
      );
    } finally {
      setSavingTeacherNavigationIds((ids) => {
        const nextIds = new Set(ids);
        nextIds.delete(navigationItem.id);
        return nextIds;
      });
    }
  };

  const handleReportingCopyChange = (itemId, field, value) => {
    setReportingCopyDrafts((drafts) => ({
      ...drafts,
      [itemId]: { ...drafts[itemId], [field]: value },
    }));
  };

  const handleReportingCopySave = async (navigationItem) => {
    const draft = reportingCopyDrafts[navigationItem.id];
    const title = draft?.title?.trim() || "";
    const subheading = draft?.subheading?.trim() || "";
    if (!title || !reportingTitleTagDefinition || !reportingSubheadingTagDefinition) {
      setTeacherNavigationError(`A title is required for ${navigationItem.label}.`);
      return;
    }

    setSavingReportingCopyIds((ids) => new Set(ids).add(navigationItem.id));
    setTeacherNavigationError(null);
    try {
      await Promise.all([
        updateEntityTag(
          TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
          navigationItem.id,
          reportingTitleTagDefinition.id,
          title,
          name
        ),
        updateEntityTag(
          TEACHER_DASHBOARD_NAVIGATION_ELEMENT_TYPE,
          navigationItem.id,
          reportingSubheadingTagDefinition.id,
          subheading,
          name
        ),
      ]);
      setReportingCopyDrafts((drafts) => ({
        ...drafts,
        [navigationItem.id]: { title, subheading },
      }));
      setSuccessMessage(`${navigationItem.label} copy saved`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setTeacherNavigationError(
        `Failed to save ${navigationItem.label} copy: ${err.message}`
      );
    } finally {
      setSavingReportingCopyIds((ids) => {
        const nextIds = new Set(ids);
        nextIds.delete(navigationItem.id);
        return nextIds;
      });
    }
  };

  const handleTcsdWorkflowToggle = async (workflowId) => {
    if (!tcsdTagDefinition || savingWorkflowIds.has(workflowId)) return;

    const previousValue = tcsdWorkflowValues[workflowId] || "ON";
    const nextValue = previousValue === "ON" ? "OFF" : "ON";
    setTcsdWorkflowValues((values) => ({ ...values, [workflowId]: nextValue }));
    setSavingWorkflowIds((ids) => new Set(ids).add(workflowId));
    setTcsdError(null);

    try {
      await updateEntityTag(
        TCSD_WORKFLOW_ELEMENT_TYPE,
        workflowId,
        tcsdTagDefinition.id,
        nextValue,
        name
      );
      setSuccessMessage(`${workflowId} switched ${nextValue}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setTcsdWorkflowValues((values) => ({ ...values, [workflowId]: previousValue }));
      setTcsdError(`Failed to update ${workflowId}: ${err.message}`);
    } finally {
      setSavingWorkflowIds((ids) => {
        const nextIds = new Set(ids);
        nextIds.delete(workflowId);
        return nextIds;
      });
    }
  };

  const handleOpenModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: true });
  };

  const handleCloseModal = (modalName) => {
    setShowModal({ ...showModal, [modalName]: false });
    setSelectedTag(null);
  };

  const handleCreateTag = async (tagData) => {
    try {
      setLoading(true);
      await createTagDefinition(tagData);
      setSuccessMessage("Tag definition created successfully");
      await fetchTagDefinitions();
      handleCloseModal('createTag');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error creating tag:", err);
      setError(`Failed to create tag: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = async (tagData) => {
    try {
      setLoading(true);
      await updateTagDefinition(selectedTag.id, tagData);
      setSuccessMessage("Tag definition updated successfully");
      await fetchTagDefinitions();
      handleCloseModal('editTag');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error updating tag:", err);
      setError(`Failed to update tag: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm("Are you sure you want to delete this tag definition? This action cannot be undone.")) {
      try {
        setLoading(true);
        await deleteTagDefinition(tagId);
        setSuccessMessage("Tag definition deleted successfully");
        await fetchTagDefinitions();
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        console.error("Error deleting tag:", err);
        setError(`Failed to delete tag: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClick = (tag) => {
    setSelectedTag(tag);
    handleOpenModal('editTag');
  };

  const getDataTypeColor = (dataType) => {
    const colors = {
      boolean: "#10b981",
      number: "#3b82f6",
      string: "#f59e0b"
    };
    return colors[dataType] || "#6b7280";
  };

  const getDataTypeBadge = (dataType) => {
    const variants = {
      boolean: "success",
      number: "primary",
      string: "warning"
    };
    return variants[dataType] || "secondary";
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner
          name={name}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Tag Management</h2>
              <p className="class-admin-page-subtitle">
                Create and manage feature tags that control functionality across schools
              </p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="mb-4">
                ✓ {successMessage}
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
                ⚠️ {error}
              </Alert>
            )}

            <Card className="mb-4 border-primary">
              <Card.Body className="d-flex justify-content-between align-items-center gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h3 className="h5 mb-0">TCSD Workflow Management</h3>
                    <Badge bg="primary">Global</Badge>
                  </div>
                  <div className="text-muted small">
                    Controls workflow availability for all TCSD users.
                  </div>
                </div>
                <Button
                  variant="primary"
                  className="flex-shrink-0"
                  onClick={() => handleOpenModal('tcsdWorkflows')}
                >
                  Manage Workflows
                </Button>
              </Card.Body>
            </Card>

            <Card className="mb-4 border-primary">
              <Card.Body className="d-flex justify-content-between align-items-center gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h3 className="h5 mb-0">Teacher Dashboard Navigation Management</h3>
                    <Badge bg="primary">Global</Badge>
                  </div>
                  <div className="text-muted small">
                    Controls left-navigation availability for all Teacher Dashboard users.
                  </div>
                </div>
                <Button
                  variant="primary"
                  className="flex-shrink-0"
                  onClick={() => handleOpenModal('teacherDashboardNavigation')}
                >
                  Manage Navigation
                </Button>
              </Card.Body>
            </Card>

            {/* Create Tag Button */}
            <div className="mb-4">
              <Button
                variant="primary"
                size="lg"
                onClick={() => handleOpenModal('createTag')}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-2" />
                Create New Tag
              </Button>
            </div>

            {/* Tags List */}
            {loading && !tagDefinitions.length ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" className="mb-3">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Loading tag definitions...</p>
              </div>
            ) : tagDefinitions.length === 0 ? (
              <Alert variant="info">
                No tag definitions exist yet. Create your first tag to get started.
              </Alert>
            ) : (
              <div className="tags-grid">
                {tagDefinitions
                  .filter(
                    (tag) =>
                      tag.tag_name !== TCSD_WORKFLOW_TAG_NAME &&
                      tag.tag_name !== TEACHER_DASHBOARD_NAVIGATION_TAG_NAME &&
                      tag.tag_name !== TEACHER_DASHBOARD_REPORTING_TITLE_TAG_NAME &&
                      tag.tag_name !== TEACHER_DASHBOARD_REPORTING_SUBHEADING_TAG_NAME
                  )
                  .map((tag) => (
                  <Card key={tag.id} className="tag-definition-card mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h5 className="mb-1">{tag.tag_name}</h5>
                          <p className="text-muted mb-2">{tag.description}</p>
                          <div className="d-flex gap-2 align-items-center">
                            <Badge bg={getDataTypeBadge(tag.data_type)}>
                              {tag.data_type}
                            </Badge>
                            <span className="text-muted small">
                              Default: <code>{tag.default_value}</code>
                            </span>
                          </div>
                        </div>
                        <div className="d-flex gap-2 ms-3">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditClick(tag)}
                          >
                            <FaEdit className="me-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTag(tag.id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                      {tag.created_by && (
                        <div className="text-muted small mt-3 pt-3 border-top">
                          Created by {tag.created_by}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      <Modal
        show={showModal.teacherDashboardNavigation}
        onHide={() => handleCloseModal('teacherDashboardNavigation')}
        centered
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            Teacher Dashboard Navigation Management
            <Badge bg="primary">Global</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="text-muted small p-3 border-bottom">
            Controls navigation availability for all Teacher Dashboard users. Existing teacher,
            school, country and role restrictions still apply.
          </div>
          {teacherNavigationError && (
            <Alert variant="danger" className="m-3">
              {teacherNavigationError}
              <Button variant="outline-danger" size="sm" className="ms-3" onClick={fetchTagDefinitions}>
                Retry
              </Button>
            </Alert>
          )}
          {teacherNavigationLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading Teacher Dashboard navigation...
            </div>
          ) : Object.keys(teacherNavigationValues).length > 0 ? (
            <div className="list-group list-group-flush">
              {TEACHER_DASHBOARD_NAVIGATION_ITEMS
                .filter((navigationItem) => !navigationItem.parentId || reportingExpanded)
                .map((navigationItem) => {
                const isSaving = savingTeacherNavigationIds.has(navigationItem.id);
                const isSavingCopy = savingReportingCopyIds.has(navigationItem.id);
                const isEnabled = navigationItem.locked ||
                  (teacherNavigationValues[navigationItem.id] || "ON") === "ON";
                const reportingParentEnabled =
                  (teacherNavigationValues.reporting || "ON") === "ON";
                const copyDraft = reportingCopyDrafts[navigationItem.id] || {
                  title: navigationItem.defaultTitle || navigationItem.label,
                  subheading: navigationItem.defaultSubheading || "",
                };
                return (
                  <div
                    key={navigationItem.id}
                    className={`list-group-item py-3 ${navigationItem.parentId ? "ps-5 bg-light" : ""}`}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <Badge bg="secondary" className="flex-shrink-0">
                        {navigationItem.number}
                      </Badge>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{navigationItem.label}</div>
                        <code className="small">{navigationItem.id}</code>
                        {navigationItem.parentId && !reportingParentEnabled && (
                          <div className="text-warning small">
                            Inactive while Reporting is off
                          </div>
                        )}
                      </div>
                      {navigationItem.id === "reporting" && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setReportingExpanded((expanded) => !expanded)}
                          aria-expanded={reportingExpanded}
                        >
                          {reportingExpanded ? "Collapse items" : "Expand items"}
                        </Button>
                      )}
                      {navigationItem.locked && (
                        <span className="text-muted small">Always available</span>
                      )}
                      {isSaving && <Spinner animation="border" size="sm" />}
                      <Form.Check
                        type="switch"
                        id={`teacher-navigation-${navigationItem.id}`}
                        aria-label={`${navigationItem.label} ${isEnabled ? "on" : "off"}`}
                        checked={isEnabled}
                        disabled={navigationItem.locked || isSaving}
                        onChange={() => handleTeacherNavigationToggle(navigationItem)}
                      />
                      <Badge bg={isEnabled ? "success" : "secondary"}>
                        {isEnabled ? "ON" : "OFF"}
                      </Badge>
                    </div>
                    {navigationItem.reportingConfig && (
                      <div className="border rounded p-3 mt-3 bg-white">
                        <Row className="g-3">
                          <Col md={5}>
                            <Form.Label htmlFor={`reporting-title-${navigationItem.id}`}>
                              Title
                            </Form.Label>
                            <Form.Control
                              id={`reporting-title-${navigationItem.id}`}
                              value={copyDraft.title}
                              onChange={(event) => handleReportingCopyChange(
                                navigationItem.id,
                                "title",
                                event.target.value
                              )}
                              disabled={isSavingCopy}
                            />
                          </Col>
                          <Col md={7}>
                            <Form.Label htmlFor={`reporting-subheading-${navigationItem.id}`}>
                              Subheading
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              id={`reporting-subheading-${navigationItem.id}`}
                              value={copyDraft.subheading}
                              onChange={(event) => handleReportingCopyChange(
                                navigationItem.id,
                                "subheading",
                                event.target.value
                              )}
                              disabled={isSavingCopy}
                            />
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-between align-items-center mt-3 gap-3">
                          <div className="text-muted small">
                            <strong>{copyDraft.title || "Untitled"}</strong>
                            {copyDraft.subheading && ` — ${copyDraft.subheading}`}
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleReportingCopySave(navigationItem)}
                            disabled={isSavingCopy || !copyDraft.title.trim()}
                          >
                            {isSavingCopy ? "Saving..." : "Save copy"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleCloseModal('teacherDashboardNavigation')}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showModal.tcsdWorkflows}
        onHide={() => handleCloseModal('tcsdWorkflows')}
        centered
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            TCSD Workflow Management
            <Badge bg="primary">Global</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="text-muted small p-3 border-bottom">
            Controls workflow availability for all TCSD users. Workflows are listed in numerical order.
          </div>
          {tcsdError && (
            <Alert variant="danger" className="m-3">
              {tcsdError}
              <Button variant="outline-danger" size="sm" className="ms-3" onClick={fetchTagDefinitions}>
                Retry
              </Button>
            </Alert>
          )}
          {tcsdLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading TCSD workflows...
            </div>
          ) : !tcsdError ? (
            <div className="list-group list-group-flush">
              {TCSD_WORKFLOWS.map((workflow) => {
                const isSaving = savingWorkflowIds.has(workflow.id);
                const isEnabled = (tcsdWorkflowValues[workflow.id] || "ON") === "ON";
                return (
                  <div
                    key={workflow.id}
                    className="list-group-item d-flex align-items-center gap-3 py-3"
                  >
                    <Badge bg="secondary" className="flex-shrink-0">
                      {workflow.number}
                    </Badge>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{workflow.label}</div>
                      <code className="small">{workflow.id}</code>
                    </div>
                    {isSaving && <Spinner animation="border" size="sm" />}
                    <Form.Check
                      type="switch"
                      id={`tcsd-workflow-${workflow.id}`}
                      aria-label={`${workflow.label} ${isEnabled ? "on" : "off"}`}
                      checked={isEnabled}
                      disabled={isSaving}
                      onChange={() => handleTcsdWorkflowToggle(workflow.id)}
                    />
                    <Badge bg={isEnabled ? "success" : "secondary"}>
                      {isEnabled ? "ON" : "OFF"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleCloseModal('tcsdWorkflows')}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Tag Modal */}
      <CreateTagDefinitionModal
        show={showModal.createTag}
        onHide={() => handleCloseModal('createTag')}
        onSubmit={handleCreateTag}
        loading={loading}
      />

      {/* Edit Tag Modal */}
      {selectedTag && (
        <EditTagDefinitionModal
          show={showModal.editTag}
          onHide={() => handleCloseModal('editTag')}
          tag={selectedTag}
          onSubmit={handleEditTag}
          loading={loading}
        />
      )}
    </div>
  );
}

export default TagManagement;
